import {
  Button,
  Card,
  Group,
  Image,
  Stack,
  Text,
  Textarea,
  Title,
} from "@mantine/core";
import { IconCamera, IconMapPin, IconRefresh } from "@tabler/icons-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  InlineError,
  SectionCard,
  StateBadge,
  StickyActionBar,
} from "../components/ui";
import { approximateDataUrlBytes, downscaleToDataUrl } from "./imageUtils";
import { SignaturePad, type SignaturePadHandle } from "./SignaturePad";
import { useGeolocation } from "./useGeolocation";
import { PROOF_REQUIREMENTS, type CapturePayload, type ProofKind } from "./types";

/**
 * The one capture component (REQ-CAP-1/2/3).
 *
 * Marketing (day order, payment), Service (collection, receiving) and
 * Delivery (proof of delivery) all use this, unmodified. REQ-CAP-2: only the
 * required-field set varies, and that comes from PROOF_REQUIREMENTS keyed by
 * `kind` - not from props - so no call site can weaken a requirement. A
 * caller that needs extra fields passes them as children.
 *
 * Submit stays disabled until every requirement for the kind is satisfied,
 * which is what makes "GPS, photo and item count before a ServiceJob can be
 * created" (REQ-SVC-1) and "all three mandatory" (REQ-DLV-2) true in the UI
 * as well as at the API.
 */

interface Props {
  kind: ProofKind;
  title: string;
  description?: string;
  submitLabel?: string;
  /**
   * What the photo is OF, when "Photo" is too vague to be useful. The
   * collection screen says "Battery photo", because the technician is
   * standing in front of a battery, a customer and a vehicle and the proof
   * has to be of the battery.
   */
  photoLabel?: string;
  /** Extra module-specific fields, rendered above the capture controls. */
  children?: React.ReactNode;
  /** Blocks submission while the caller's own fields are incomplete. */
  disabled?: boolean;
  onSubmit: (payload: CapturePayload) => Promise<void>;
  onCancel?: () => void;
}

export function CaptureSheet({
  kind,
  title,
  description,
  submitLabel = "Submit",
  photoLabel = "Photo",
  children,
  disabled = false,
  onSubmit,
  onCancel,
}: Props) {
  const requirements = PROOF_REQUIREMENTS[kind];
  const geo = useGeolocation(requirements.gps);
  const signatureRef = useRef<SignaturePadHandle | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [photoDataUrl, setPhotoDataUrl] = useState<string>();
  const [photoError, setPhotoError] = useState<string>();
  const [photoBusy, setPhotoBusy] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>();

  const gpsReady = !requirements.gps || geo.state.status === "ready";
  const photoReady = !requirements.photo || Boolean(photoDataUrl);
  const signatureReady = !requirements.signature || hasSignature;
  const canSubmit =
    gpsReady && photoReady && signatureReady && !disabled && !submitting && !photoBusy;

  const missing = useMemo(() => {
    const items: string[] = [];
    if (!gpsReady) items.push("location");
    if (!photoReady) items.push("photo");
    if (!signatureReady) items.push("signature");
    return items;
  }, [gpsReady, photoReady, signatureReady]);

  /**
   * What is stopping the button, as a sentence.
   *
   * "Still needed: signature" is a list of one, and a list of one reads as a
   * form telling you off rather than as an answer. When exactly one thing is
   * outstanding it is NAMED - which is what the brief asks for, and what
   * somebody standing at a customer's door actually needs: not "something is
   * missing" but "the signature is the thing".
   */
  const blocker = useMemo(() => {
    if (missing.length === 0) return undefined;
    if (missing.length === 1) {
      const one = missing[0]!;
      return one === "signature"
        ? "The signature is the one thing still missing."
        : one === "photo"
          ? "The photo is still needed before this can be recorded."
          : "Your location is still needed before this can be recorded.";
    }
    // Two or three: an "and" rather than a comma, because this is read aloud
    // in a hurry and "location, photo" looks like one item with a comma in it.
    const last = missing[missing.length - 1]!;
    return `Still needed: ${missing.slice(0, -1).join(", ")} and ${last}.`;
  }, [missing]);

  /**
   * The connection-lost state, which replaces the old offline banner.
   *
   * WHAT IT DOES NOT SAY
   *
   * The brief's copy is "the handover is sent when you are back online". That
   * would be a lie: there is no sync queue behind this (offline capture is a
   * documented deferral, REQ-NFR-2), so nothing is held and nothing is sent
   * later. A banner promising a queue that does not exist is worse than no
   * banner - an agent would walk away from the door believing the handover
   * was recorded.
   *
   * So it says what is true: nothing has been sent, stay here, try again when
   * the signal is back, and leaving loses what you have captured.
   */
  const [online, setOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine,
  );
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);

  async function handleFile(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    // Clear immediately so picking the same file twice still fires onChange.
    event.target.value = "";
    if (!file) return;

    setPhotoError(undefined);
    setPhotoBusy(true);
    try {
      setPhotoDataUrl(await downscaleToDataUrl(file));
    } catch (error) {
      setPhotoError(error instanceof Error ? error.message : "Could not read that photo.");
    } finally {
      setPhotoBusy(false);
    }
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;

    setSubmitError(undefined);
    setSubmitting(true);
    try {
      await onSubmit({
        kind,
        ...(geo.state.status === "ready" ? { gps: geo.state.fix } : {}),
        ...(photoDataUrl ? { photoDataUrl } : {}),
        ...(requirements.signature
          ? { signatureDataUrl: signatureRef.current?.toDataUrl() }
          : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        clientCapturedAt: new Date().toISOString(),
      });
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : "Could not save. Try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="md">
        <Stack gap={2}>
          <Title order={5}>{title}</Title>
          {description && (
            <Text fz="md" c="gray.7">
              {description}
            </Text>
          )}
        </Stack>

        {!online && (
          <Card
            withBorder
            padding={12}
            style={{ background: "#fff9db", borderColor: "#ffe066" }}
          >
            <Text fz={14} fw={600}>
              No signal right now.
            </Text>
            <Text fz={13} c="gray.7">
              Nothing has been sent yet. Stay on this screen and try again when
              the signal comes back — leaving it loses the photo and the
              signature you have taken.
            </Text>
          </Card>
        )}

        {children}

        {requirements.gps && (
          <SectionCard title="Location" required>
            {geo.state.status === "locating" && (
              <Group gap="xs" wrap="nowrap">
                <IconMapPin size={18} stroke={1.6} color="var(--mantine-color-gray-6)" />
                <Text fz="md" c="gray.7">
                  Getting a precise location…
                  {geo.state.best
                    ? ` best so far ±${geo.state.best.accuracyMetres} m`
                    : ""}
                </Text>
              </Group>
            )}
            {geo.state.status === "ready" && (
              <Stack gap="xs">
                <Group gap="sm" wrap="wrap">
                  {/* A label AND a colour, never the colour alone: "coarser
                      than ±N m" is the part that matters and it is written
                      out, not implied by the tint. */}
                  <StateBadge
                    state={geo.isPrecise ? "svcServiced" : "dlvPending"}
                    label={geo.isPrecise ? "Located" : "Rough fix"}
                  />
                  <Text fz="md" c="gray.7" className="num">
                    {geo.state.fix.lat.toFixed(6)}, {geo.state.fix.long.toFixed(6)} · ±
                    {geo.state.fix.accuracyMetres} m
                  </Text>
                </Group>
                {!geo.isPrecise && (
                  <Group gap="sm" wrap="nowrap">
                    <Text fz="sm" c="gray.6">
                      Coarser than ±{geo.goodAccuracyMetres} m.
                    </Text>
                    <Button
                      variant="default"
                      size="sm"
                      leftSection={<IconRefresh size={15} stroke={1.8} />}
                      onClick={geo.retry}
                    >
                      Improve
                    </Button>
                  </Group>
                )}
              </Stack>
            )}
            {geo.state.status === "error" && (
              <Stack gap="xs">
                <InlineError>{geo.state.message}</InlineError>
                {geo.state.canRetry && (
                  <Button
                    variant="default"
                    size="sm"
                    leftSection={<IconRefresh size={15} stroke={1.8} />}
                    onClick={geo.retry}
                    style={{ alignSelf: "flex-start" }}
                  >
                    Retry
                  </Button>
                )}
              </Stack>
            )}
          </SectionCard>
        )}

        {requirements.photo && (
          <SectionCard title={photoLabel} required>
            <Stack gap="sm">
              {photoDataUrl ? (
                <>
                  <Image
                    src={photoDataUrl}
                    alt="Captured proof"
                    radius="md"
                    fit="cover"
                    mah={280}
                  />
                  <Group justify="space-between" gap="sm">
                    <Text fz="sm" c="gray.6" className="num">
                      {(approximateDataUrlBytes(photoDataUrl) / 1024).toFixed(0)} KB
                    </Text>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Retake
                    </Button>
                  </Group>
                </>
              ) : (
                <Button
                  variant="default"
                  size="md"
                  fullWidth
                  leftSection={<IconCamera size={18} stroke={1.8} />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={photoBusy}
                  loading={photoBusy}
                >
                  {photoBusy ? "Processing…" : "Take photo"}
                </Button>
              )}

              {/* `capture="environment"` opens the rear camera directly on
                  mobile. A file input rather than getUserMedia, deliberately -
                  see imageUtils.ts. On desktop it falls back to a file picker,
                  which is what an Admin reviewing from a laptop would want. */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="visually-hidden"
                onChange={handleFile}
              />
              {photoError && <InlineError>{photoError}</InlineError>}
            </Stack>
          </SectionCard>
        )}

        {requirements.signature && (
          <SectionCard title="Customer signature" required>
            <SignaturePad
              ref={signatureRef}
              onChange={setHasSignature}
              ariaLabel="Customer signature"
            />
          </SectionCard>
        )}

        <Textarea
          id="capture-notes"
          label="Notes"
          description="Optional."
          rows={2}
          value={notes}
          onChange={(event) => setNotes(event.currentTarget.value)}
          maxLength={1000}
          placeholder="Anything worth recording"
        />

        {submitError && <InlineError>{submitError}</InlineError>}

        <StickyActionBar
          secondary={
            onCancel ? (
              <Button variant="subtle" color="gray" onClick={onCancel}>
                Cancel
              </Button>
            ) : undefined
          }
          context={blocker}
          primary={
            <Button
              type="submit"
              size="md"
              fullWidth
              disabled={!canSubmit}
              loading={submitting}
            >
              {submitting ? "Saving…" : submitLabel}
            </Button>
          }
        />
      </Stack>
    </form>
  );
}
